import { get, isEmpty } from "lodash";
import { Close, Icon } from "@shared/ui";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-easy-crop";
import toast from "react-hot-toast";

import {
  generateSignedUrl,
  invalidateClientConfigCache,
  updateClientConfigWithLogo,
  uploadDocument,
} from "../lib/automatic";
import ButtonPrimary from "./ButtonPrimary";
import ButtonSecondary from "./ButtonSecondary";

const ACCEPT_TYPES = "image/png,image/svg+xml";
const MAX_LOGO_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_LOGO_SIZE_LABEL = "5 MB";
const CROP_ASPECT_RATIO = 1;

const newLine = (
  <li className="text-sm" style={{ listStyleType: "none" }}>
    &nbsp;
  </li>
);

const getCroppedPngBlob = (imageSrc, croppedAreaPixels) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      const context = canvas.getContext("2d");
      if (!context) {
        reject(new Error("Could not initialize crop canvas"));
        return;
      }

      context.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Could not create cropped image"));
          return;
        }
        resolve(blob);
      }, "image/png");
    };
    image.onerror = () =>
      reject(new Error("Could not load image for cropping"));
    image.src = imageSrc;
  });

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result) {
        resolve(reader.result);
      } else {
        reject(new Error("Could not read file"));
      }
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("Could not read file"));
    reader.readAsArrayBuffer(file);
  });

/**
 * Modal for admins to set the dashboard client logo URL (stored on the Axle client record).
 * Uploads the selected image to the labs assets bucket, then saves logoUrl on the client.
 * @param clientDisplayName - Shown in the sidebar-style preview row
 * @param clientEntity - Shown under the display name in the preview row
 */
const ClientLogoEditDialog = ({
  isOpen,
  onClose,
  clientId,
  clientDisplayName,
  clientEntity,
  currentLogoUrl,
  onSaved,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadPayload, setUploadPayload] = useState(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState(null);
  const [pendingCropImageUrl, setPendingCropImageUrl] = useState(null);
  const [pendingCropFileName, setPendingCropFileName] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [fileError, setFileError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedFile(null);
      setUploadPayload(null);
      setPreviewObjectUrl(null);
      setPendingCropImageUrl(null);
      setPendingCropFileName("");
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setFileError(null);
    }
  }, [isOpen, currentLogoUrl]);

  useEffect(() => {
    return () => {
      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl);
      }
      if (pendingCropImageUrl) {
        URL.revokeObjectURL(pendingCropImageUrl);
      }
    };
  }, [pendingCropImageUrl, previewObjectUrl]);

  if (!isOpen) {
    return null;
  }

  const previewDisplayUrl = previewObjectUrl || currentLogoUrl || null;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    const isSvgFile = file?.type === "image/svg+xml";
    if (!file) {
      return;
    }
    if (!ACCEPT_TYPES.split(",").includes(file.type)) {
      setFileError("Upload a PNG or SVG file");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setFileError(`Upload a PNG or SVG under ${MAX_LOGO_SIZE_LABEL}`);
      e.target.value = "";
      return;
    }
    setFileError(null);
    if (previewObjectUrl) {
      URL.revokeObjectURL(previewObjectUrl);
      setPreviewObjectUrl(null);
    }
    // SVGs are uploaded as-is. PNGs go through the crop flow so users can adjust placement.
    if (isSvgFile) {
      handleCancelCrop();
      try {
        const fileData = await readFileAsArrayBuffer(file);
        setUploadPayload({ fileData, contentType: file.type });
        setSelectedFile(file);
        setPreviewObjectUrl(URL.createObjectURL(file));
      } catch (error) {
        console.error("[ClientLogoEditDialog] file read failed", error);
        setFileError("Could not read image file");
      }
      e.target.value = "";
      return;
    }
    if (pendingCropImageUrl) {
      URL.revokeObjectURL(pendingCropImageUrl);
    }
    setUploadPayload(null);
    setSelectedFile(null);
    setPendingCropImageUrl(URL.createObjectURL(file));
    setPendingCropFileName(file.name);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    e.target.value = "";
  };

  const handleCancelCrop = () => {
    if (pendingCropImageUrl) {
      URL.revokeObjectURL(pendingCropImageUrl);
    }
    setPendingCropImageUrl(null);
    setPendingCropFileName("");
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleApplyCrop = async () => {
    if (!pendingCropImageUrl || !croppedAreaPixels) {
      toast.error("Adjust the crop area first");
      return;
    }

    try {
      const croppedBlob = await getCroppedPngBlob(
        pendingCropImageUrl,
        croppedAreaPixels
      );
      const sanitizedName = pendingCropFileName
        .replace(/\.[^/.]+$/, "")
        .replace(/\s+/g, "-")
        .toLowerCase();
      const croppedFile = new File(
        [croppedBlob],
        `${sanitizedName || "logo"}-cropped.png`,
        {
          type: "image/png",
        }
      );

      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl);
      }

      const croppedBytes = await readFileAsArrayBuffer(croppedBlob);
      setUploadPayload({ fileData: croppedBytes, contentType: "image/png" });
      setSelectedFile(croppedFile);
      setPreviewObjectUrl(URL.createObjectURL(croppedFile));
      handleCancelCrop();
    } catch (error) {
      console.error("[ClientLogoEditDialog] crop failed", error);
      toast.error("Could not crop image. Try a different PNG.");
    }
  };

  const handleEditCropFromPreview = () => {
    if (!selectedFile) {
      toast.error("Upload an image first to adjust crop");
      return;
    }
    if (selectedFile.type !== "image/png") {
      toast.error("SVG uploads are used as-is");
      return;
    }
    if (pendingCropImageUrl) {
      URL.revokeObjectURL(pendingCropImageUrl);
    }
    setPendingCropImageUrl(URL.createObjectURL(selectedFile));
    setPendingCropFileName(selectedFile.name);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (!uploadPayload) {
        toast.error("Choose an image file");
        return;
      }
      // Mirrors the Ignition ManualPolicyDocument flow: presign, then PUT
      // the file to the signed URL (its type becomes the Content-Type)
      const [signedUrlResponse, signedUrlResponseError] =
        await generateSignedUrl(clientId, uploadPayload.contentType);

      if (signedUrlResponseError) {
        toast.error(signedUrlResponseError.message || "Could not start upload");
        return;
      }

      if (
        isEmpty(get(signedUrlResponse, "signedUrl")) ||
        isEmpty(get(signedUrlResponse, "logoUrl"))
      ) {
        console.error("Signed URL is empty");
        toast.error("Could not start upload");
        return;
      }

      const [, putErr] = await uploadDocument(
        signedUrlResponse.signedUrl,
        uploadPayload.fileData,
        uploadPayload.contentType
      );
      if (putErr) {
        toast.error(putErr.message || "Upload failed");
        return;
      }

      const [data, error] = await updateClientConfigWithLogo(
        clientId,
        signedUrlResponse.logoUrl
      );
      if (error) {
        toast.error(error.message || "Could not save logo URL");
        return;
      }
      invalidateClientConfigCache(clientId);
      onSaved({ ...data, logoUrl: signedUrlResponse.logoUrl });
      toast.success("Logo updated");
      onClose();
    } catch (err) {
      console.error("[ClientLogoEditDialog] save failed", err);
      toast.error(err?.message || "Something went wrong");
    } finally {
      setIsSaving(false);
    }
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2
              id="client-logo-dialog-title"
              className="text-xl font-semibold text-gray-900"
            >
              Update client logo
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 focus:outline-none"
              aria-label="Close"
              disabled={isSaving}
            >
              <Icon icon={Close} className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="mb-4">
              <div className="border-2 border-dashed border-gray-300 rounded-sm p-6 bg-[#F4F4F5]">
                <ul className="list-disc pl-5 text-gray-500">
                  <li className="text-sm flex flex-col gap-1">
                    <span>
                      <strong className="text-black">
                        Accepted formats (Max size: {MAX_LOGO_SIZE_LABEL})
                      </strong>
                    </span>
                  </li>
                  {newLine}
                  <li className="text-sm flex flex-col gap-1">
                    <span>
                      <strong className="text-black">PNG</strong> — if the image
                      is too large, you'll have the option to crop it before
                      upload
                    </span>
                  </li>
                  {newLine}
                  <li className="text-sm flex flex-col gap-1">
                    <span>
                      <strong className="text-black">SVG</strong> — All the SVG
                      files are uploaded as-is
                    </span>
                  </li>
                </ul>
              </div>

              <input
                ref={fileInputRef}
                id="client-logo-file"
                style={{ display: "none" }}
                type="file"
                accept={ACCEPT_TYPES}
                aria-label="Upload client logo"
                onChange={handleFileChange}
              />

              <div className="mt-4">
                <ButtonPrimary
                  text="Upload"
                  onClick={() => fileInputRef.current?.click()}
                  type="button"
                  className="w-full justify-center"
                />
              </div>

              {fileError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm text-red-800">
                    <span className="font-medium">Warning:</span> {fileError}
                  </p>
                </div>
              )}
            </div>

            {previewDisplayUrl && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">
                  Preview
                </h3>
                <div className="flex items-center gap-3">
                  {selectedFile && selectedFile.type === "image/png" ? (
                    <button
                      type="button"
                      className="h-12 w-12 shrink-0 rounded-full border border-gray-200 bg-white bg-cover bg-center bg-no-repeat cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-300"
                      style={{
                        backgroundImage: `url('${previewDisplayUrl}')`,
                      }}
                      aria-label="Preview logo, click to adjust crop"
                      onClick={handleEditCropFromPreview}
                    />
                  ) : (
                    <div
                      className="h-12 w-12 shrink-0 rounded-full border border-gray-200 bg-white bg-cover bg-center bg-no-repeat"
                      style={{
                        backgroundImage: `url('${previewDisplayUrl}')`,
                      }}
                      role="img"
                      aria-label="Logo preview"
                    />
                  )}
                  <div className="min-w-0 flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {clientDisplayName || "—"}
                    </span>
                    {clientEntity && (
                      <span className="text-xs text-gray-500 truncate">
                        {clientEntity}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-x-3">
              <ButtonSecondary
                text="Cancel"
                onClick={onClose}
                type="button"
                disabled={isSaving}
              />
              <ButtonPrimary
                text={isSaving ? "Saving…" : "Save"}
                onClick={handleSave}
                type="button"
                disabled={isSaving}
              />
            </div>
          </div>
        </div>
      </div>

      {pendingCropImageUrl && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50"
          onClick={handleCancelCrop}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Crop logo</h3>
              <button
                type="button"
                onClick={handleCancelCrop}
                className="p-1 rounded hover:bg-gray-100 focus:outline-none"
                aria-label="Close crop dialog"
              >
                <Icon icon={Close} className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">
                Move and zoom to choose what appears inside the client-logo
                circle.
              </p>
              <div className="relative h-72 w-full bg-gray-900 rounded-md overflow-hidden">
                <Cropper
                  image={pendingCropImageUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={CROP_ASPECT_RATIO}
                  cropShape="round"
                  showGrid
                  onCropChange={setCrop}
                  onCropComplete={(_, areaPixels) =>
                    setCroppedAreaPixels(areaPixels)
                  }
                  onZoomChange={setZoom}
                />
              </div>
              <div className="mt-4">
                <label
                  className="block text-sm font-medium text-gray-700 mb-2"
                  htmlFor="logo-crop-zoom"
                >
                  Zoom
                </label>
                <input
                  id="logo-crop-zoom"
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(event) => setZoom(Number(event.target.value))}
                  className="w-full accent-black"
                />
              </div>
              <div className="mt-6 flex justify-end gap-x-3">
                <ButtonSecondary
                  text="Cancel crop"
                  onClick={handleCancelCrop}
                  type="button"
                />
                <ButtonPrimary
                  text="Use cropped image"
                  onClick={handleApplyCrop}
                  type="button"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};

export default ClientLogoEditDialog;
